import { useEffect, useRef, useState } from 'react';
import { Button, Stack } from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import type { MeteredUsage } from 'api-client';
import { useSubscriptionsApi, useUser } from '@/context';
import { billingErrorMessage } from '../billingRequests';
import { useBillingDemo } from './useBillingDemo';
import BillingDemoPanel from './BillingDemoPanel';
import MeteredUsagePanel from './MeteredUsagePanel';
export default function MeteredBillingDemo() {
  const { teamSlug = '' } = useParams<{ teamSlug: string }>();
  return <MeteredSession key={teamSlug} teamSlug={teamSlug} />;
}
function MeteredSession({ teamSlug }: { teamSlug: string }) {
  const api = useSubscriptionsApi();
  const user = useUser();
  const resource = useBillingDemo(teamSlug);
  const [itemId, setItem] = useState<string>('');
  const [quantity, setQuantity] = useState<string>('1');
  const [pending, setPending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<MeteredUsage | null>(null);
  const mounted = useRef<boolean>(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (!itemId && resource.data?.meteredItems.length) setItem(resource.data.meteredItems[0].value);
  }, [resource.data, itemId]);
  const submit = async () => {
    if (pending || !resource.data?.billingEnabled || !resource.data.hasAccess || !itemId) return;
    setPending(true);
    setError(null);
    setReceipt(null);
    try {
      const record = await api.subscriptionUsageCreate({
        teamSlug,
        meteredUsageInput: { subscriptionItem: itemId, quantity: Number(quantity) },
      });
      if (!mounted.current) return;
      setReceipt(record);
      setQuantity('1');
      resource.setData((value) =>
        value
          ? { ...value, usageRecords: [record, ...value.usageRecords.filter((item) => item.id !== record.id)] }
          : value,
      );
    } catch (failure) {
      const message = await billingErrorMessage(failure);
      if (mounted.current) setError(message);
    } finally {
      if (mounted.current) setPending(false);
    }
  };
  return (
    <Stack spacing={3}>
      <BillingDemoPanel
        title="Metered Billing Demo"
        data={resource.data}
        loading={resource.loading}
        error={resource.error}
        onRetry={resource.retry}
      />
      {resource.data && (
        <MeteredUsagePanel
          data={resource.data}
          itemId={itemId}
          quantity={quantity}
          pending={pending}
          error={error}
          receipt={receipt}
          timeZone={user?.timezone || undefined}
          onItem={setItem}
          onQuantity={setQuantity}
          onSubmit={() => void submit()}
        />
      )}
      <Button component={RouterLink} to={`/a/${teamSlug}/subscription/demo`}>
        Back to Subscription Demo
      </Button>
    </Stack>
  );
}
